import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Key,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Save,
  Brain,
  Search,
  Code,
  Zap,
  Link,
  MapPin,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import { getErrorMessage } from '../../lib/utils';


interface GeminiConfig {
  id: string;
  tenant_id: string;
  api_key_hint: string;
  default_model: string;
  is_active: boolean;
  connection_valid: boolean;
  last_tested_at: string | null;
}

export default function GeminiAIConfigTab() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState<GeminiConfig | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string; models?: string[] } | null>(null);

  const [apiKey, setApiKey] = useState('');
  const [isActive, setIsActive] = useState(false);
  const initialLoadDone = useRef(false);
  const [savingToggle, setSavingToggle] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const configRes = await apiClient.get('/ai-tools/gemini-config');
      const configData = (configRes?.data ?? configRes) as GeminiConfig | null;
      setConfig(configData);
      if (configData) {
        setIsActive(configData.is_active);
        if (configData.last_tested_at && !testResult) {
          setTestResult({
            success: configData.connection_valid,
            models: configData.connection_valid ? ['Connessione verificata'] : undefined,
            error: configData.connection_valid ? undefined : 'Ultimo test fallito',
          });
        }
      }
      initialLoadDone.current = true;
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [testResult, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleActive = useCallback(async (checked: boolean) => {
    setIsActive(checked);
    if (initialLoadDone.current && config) {
      setSavingToggle(true);
      try {
        await apiClient.put('/ai-tools/gemini-config', { is_active: checked });
      } catch (error) {
        toast({ title: 'Errore salvataggio', description: getErrorMessage(error), variant: 'destructive' });
        await fetchData();
      } finally {
        setSavingToggle(false);
      }
    }
  }, [config, toast, fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: { is_active: boolean; api_key?: string } = {
        is_active: isActive,
      };
      if (apiKey) {
        payload.api_key = apiKey;
      }
      await apiClient.put('/ai-tools/gemini-config', payload);
      toast({ title: 'Salvato', description: 'Configurazione Gemini AI aggiornata' });
      setApiKey('');
      await fetchData();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      if (!apiKey && !config?.api_key_hint) {
        toast({ title: 'Errore', description: 'Inserisci una API Key per testare la connessione', variant: 'destructive' });
        setTesting(false);
        return;
      }
      const res = await apiClient.post('/ai-tools/gemini-config/test', {
        api_key: apiKey || undefined,
      });
      const raw = res?.data || res;
      const result = ((raw as { data?: unknown })?.data || raw) as { success: boolean; models?: string[]; error?: string };
      setTestResult(result);
      if (result.success) {
        toast({ title: 'Connessione OK', description: `${result.models?.length || 0} modelli disponibili` });
        await fetchData();
      } else {
        toast({ title: 'Connessione fallita', description: result.error, variant: 'destructive' });
        await fetchData();
      }
    } catch (error) {
      setTestResult({ success: false, error: getErrorMessage(error) });
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Brain className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gemini AI (Google)</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Provider AI Google — il modello si sceglie nella configurazione agente</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Label htmlFor="gemini-active" className="text-sm text-gray-700 dark:text-gray-300">Attivo</Label>
            <Switch
              id="gemini-active"
              checked={isActive}
              onCheckedChange={handleToggleActive}
              disabled={savingToggle}
            />
            {isActive ? (
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Attivo</Badge>
            ) : (
              <Badge className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">Disattivato</Badge>
            )}
          </div>
        </div>

        <div className="mb-4">
          <Label className="text-gray-700 dark:text-gray-300 text-sm">API Key</Label>
          <div className="relative mt-1">
            <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={config?.api_key_hint || 'AIza...'}
              className="pl-10 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleTest}
            disabled={testing || (!apiKey && !config?.api_key_hint)}
            variant="outline"
            className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/20"
          >
            {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Test Connessione
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salva Configurazione
          </Button>
        </div>

        <div className={`mt-4 p-3 rounded-lg border ${config?.connection_valid ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : config?.last_tested_at ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'}`}>
          <div className="flex items-center gap-2">
            {config?.connection_valid ? (
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            ) : config?.last_tested_at ? (
              <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
            ) : (
              <XCircle className="h-4 w-4 text-gray-400" />
            )}
            <span className={`text-sm font-medium ${config?.connection_valid ? 'text-green-700 dark:text-green-400' : config?.last_tested_at ? 'text-red-700 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
              Stato: {config?.connection_valid ? 'Connesso' : config?.last_tested_at ? 'Non connesso' : 'Mai testato'}
            </span>
            {config?.last_tested_at && (
              <span className="text-xs text-gray-400 ml-auto">
                Ultimo test: {new Date(config.last_tested_at).toLocaleString('it-IT')}
              </span>
            )}
          </div>
        </div>

        {testResult && (
          <div className={`mt-3 p-3 rounded-lg border ${testResult.success ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
            <div className="flex items-center gap-2">
              {testResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
              )}
              <span className={`text-sm font-medium ${testResult.success ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                {testResult.success ? 'Connessione riuscita' : 'Connessione fallita'}
              </span>
            </div>
            {testResult.success && testResult.models && testResult.models.length > 0 && testResult.models[0] !== 'Connessione verificata' && (
              <div className="mt-2 space-y-1">
                <p className="text-xs font-medium text-green-700 dark:text-green-400">
                  {testResult.models.length} modelli disponibili:
                </p>
                <div className="flex flex-wrap gap-1">
                  {testResult.models.slice(0, 10).map((model, i) => (
                    <Badge key={i} className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 text-[10px]">
                      {model}
                    </Badge>
                  ))}
                  {testResult.models.length > 10 && (
                    <Badge className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-[10px]">
                      +{testResult.models.length - 10} altri
                    </Badge>
                  )}
                </div>
              </div>
            )}
            {!testResult.success && testResult.error && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{testResult.error}</p>
            )}
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tool Nativi Gemini</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Funzionalità native integrate nel modello</p>
          </div>
        </div>
        <div className="mb-3 p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            <strong>Gemini 3 (aprile 2026):</strong> usa automaticamente <code>thinkingLevel</code> (LOW/MEDIUM/HIGH) al posto del legacy <code>thinkingBudget</code>, e propaga le <code>thoughtSignature</code> obbligatorie nelle conversazioni multi-turn con tool calling. Per Gemini 2.x il comportamento resta invariato (<code>thinkingBudget</code>).
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Search className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">google_search</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Ricerca web in tempo reale tramite Google (alternativa a Firecrawl/SerpAPI, costo zero extra)</p>
            </div>
            <Badge className="ml-auto bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px]">Chat + Voice</Badge>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Code className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">code_execution</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Esecuzione codice Python nel sandbox</p>
            </div>
            <Badge className="ml-auto bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px]">Solo Chat</Badge>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Link className="h-4 w-4 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">url_context</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Lettura contenuti da URL nel prompt</p>
            </div>
            <Badge className="ml-auto bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px]">Solo Chat</Badge>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <MapPin className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">google_maps</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Dati geolocalizzati tramite Google Maps</p>
            </div>
            <Badge className="ml-auto bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px]">Solo Chat</Badge>
          </div>
        </div>
      </div>
    </div>
  );
}
